import {
	queryOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';

import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { fillData } from '../shared/data';
import {
	Categories,
	Data,
	DataBody,
	DataErrorKeys,
} from '../shared/data.types';
import { riskQueries } from './risks';

const getRiskData = (riskId: string) =>
	api
		.get(`/file/documentdata?riskId=${riskId}`)
		.then(parseSchema(Data))
		.then(fillData)
		.catch(handleError(DataErrorKeys));

const getExpenseCategories = () =>
	api.get('/risks/expense-categories').then(parseSchema(Categories));

const getRevenueCategories = () =>
	api.get('/risks/revenue-categories').then(parseSchema(Categories));

export const riskDataQueries = {
	all: ['riskData'],
	id: (riskId: string) =>
		queryOptions({
			queryKey: [...riskQueries.ids(), riskId, 'data'],
			queryFn: () => getRiskData(riskId),
		}),
	expenseCategories: () =>
		queryOptions({
			queryKey: [...riskDataQueries.all, 'expenseCategories'],
			queryFn: () => getExpenseCategories(),
		}),
	revenueCategories: () =>
		queryOptions({
			queryKey: [...riskDataQueries.all, 'revenueCategories'],
			queryFn: () => getRevenueCategories(),
		}),
};

const putRiskData = (riskId: string, data: DataBody) =>
	api.put(`/file/documentdata?riskId=${riskId}`, { json: data });

export const useMutateRiskData = (riskId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: DataBody) => putRiskData(riskId, data),
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), riskId],
				}),
			]);
		},
	});
};
